import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStaffAuth } from '@/features/auth/useStaffAuth';

/**
 * Staff sign-in (slice 17).
 *
 * The subtitle names who this portal is for. `/auth/login` authenticates BOTH
 * principals — a customer's credentials are genuinely valid against it — so a
 * tenant owner who lands here would otherwise get a correct password followed by
 * a confusing refusal. Saying it up front is cheaper than explaining it after.
 */
export function Login() {
  const { t } = useTranslation();
  const { login } = useStaffAuth();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await login(phone.trim(), password);
    if (!res.ok) setError(res.reason);
    setBusy(false);
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-raad-bg px-5 py-10 text-raad-text">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5">
          <span className="size-2.5 rounded-full bg-raad-primary" />
          <h1 className="font-display text-xl font-bold tracking-tight">{t('login.title')}</h1>
        </div>
        <p className="mt-2 text-sm text-raad-muted">{t('login.subtitle')}</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-3" noValidate>
          <label className="block">
            <span className="text-[11.5px] font-semibold text-raad-muted">{t('login.phone')}</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="username"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
            />
          </label>

          <label className="block">
            <span className="text-[11.5px] font-semibold text-raad-muted">{t('login.password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
            />
          </label>

          {error !== null ? (
            <p role="alert" className="text-[12.5px] text-raad-error">
              {t(`login.error.${error}`)}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy || phone.trim() === '' || password === ''}
            className="min-h-11 w-full rounded-xl bg-raad-primary px-4 text-sm font-semibold text-raad-onprimary transition-colors hover:bg-raad-accent disabled:opacity-50"
          >
            {busy ? t('login.signingIn') : t('login.submit')}
          </button>
        </form>
      </div>
    </main>
  );
}

export default Login;
